import { useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, KeyRound } from 'lucide-react';
import type { LearningAccessInput } from '@lms/shared/types';
import { apiClient } from '../lib/api-client';
import { useLanguage } from '../lib/LanguageContext';
import { learningAccessCopy as words } from '../copy/learning-access-copy';

export function ParticipantLearningAccess({ enrollmentId }: { enrollmentId: string }) {
  const { t } = useLanguage();
  const prefix = useId();
  const cache = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [levelId, setLevelId] = useState('');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [allSessions, setAllSessions] = useState(false);
  const [reason, setReason] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [saved, setSaved] = useState(false);
  const report = useQuery({
    queryKey: ['prior-learning', enrollmentId],
    queryFn: () => apiClient.priorLearning.report(enrollmentId),
    enabled: expanded,
  });
  const subjects = report.data?.subjects ?? [];
  const levels = [
    ...new Map(
      subjects
        .filter(item => item.levelId)
        .map(item => [item.levelId!, item.levelName ?? item.location] as const)
    ).entries(),
  ];
  const filtered = subjects.filter(
    item =>
      (!levelId || item.levelId === levelId) &&
      `${item.name} ${item.code ?? ''} ${item.location}`
        .toLocaleLowerCase()
        .includes(search.trim().toLocaleLowerCase())
  );
  const selected = subjects.filter(item => selectedIds.includes(item.id));
  const mutation = useMutation({
    mutationFn: (input: LearningAccessInput) =>
      apiClient.priorLearning.setAccess(enrollmentId, input),
    onSuccess: updated => {
      cache.setQueryData(['prior-learning', enrollmentId], updated);
      for (const key of ['enrollments', 'users', 'programs', 'evaluations'])
        void cache.invalidateQueries({ queryKey: [key] });
      setSaved(true);
      setSelectedIds([]);
      setReason('');
      setConfirmation('');
    },
    onError: () => {
      setConfirmation('');
      void report.refetch();
    },
  });
  const busy = mutation.isPending;
  const valid =
    !report.isError &&
    !report.isFetching &&
    selected.length > 0 &&
    selected.length <= 100 &&
    selected.length === selectedIds.length &&
    reason.trim().length >= 5 &&
    reason.length <= 1000;
  const submit = (action: 'BUKA' | 'CABUT') => {
    setSaved(false);
    mutation.mutate({
      subjects: selected.map(item => ({
        subjectId: item.id,
        expectedUpdatedAt: item.accessGrant?.updatedAt ?? null,
      })),
      reason: reason.trim(),
      confirmation: action,
      unlockAllSessions: allSessions,
    });
  };
  const controls =
    'min-h-12 w-full rounded-lg bg-surface-container-high px-4 py-3 text-base text-on-surface focus-visible:outline-2 focus-visible:outline-primary';
  return (
    <section className="rounded-2xl bg-surface-container-low p-4 text-base text-on-surface sm:p-5">
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={`${prefix}-panel`}
        onClick={() => setExpanded(!expanded)}
        className="flex min-h-12 w-full items-center justify-between gap-3 text-start font-headline font-bold focus-visible:outline-2 focus-visible:outline-primary"
      >
        <span className="flex items-center gap-2">
          <KeyRound size={22} aria-hidden="true" />
          {t(words.title)}
        </span>
        <ChevronDown aria-hidden="true" className={expanded ? 'rotate-180' : ''} />
      </button>
      {expanded && (
        <div id={`${prefix}-panel`} className="mt-4 space-y-5">
          <p className="leading-relaxed text-on-surface-variant">{t(words.intro)}</p>
          {report.isLoading && <p role="status">{t(words.loading)}</p>}
          {report.isError && (
            <div role="alert">
              <p>{t(words.loadError)}</p>
              <button
                type="button"
                className="min-h-12 font-bold text-primary"
                onClick={() => void report.refetch()}
              >
                {t(words.retry)}
              </button>
            </div>
          )}
          {report.data && (
            <>
              <p className="font-semibold">
                {t(words.summary)
                  .replace('{credited}', String(subjects.filter(item => item.recognition).length))
                  .replace(
                    '{granted}',
                    String(subjects.filter(item => item.accessGrant?.active).length)
                  )}
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor={`${prefix}-level`} className="mb-2 block font-semibold">
                    {t(words.level)}
                  </label>
                  <select
                    id={`${prefix}-level`}
                    value={levelId}
                    disabled={busy}
                    className={controls}
                    onChange={event => {
                      setLevelId(event.target.value);
                      setSelectedIds([]);
                      setConfirmation('');
                    }}
                  >
                    <option value="">{t(words.allLevels)}</option>
                    {levels.map(([id, name]) => (
                      <option key={id} value={id}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor={`${prefix}-search`} className="mb-2 block font-semibold">
                    {t(words.search)}
                  </label>
                  <input
                    id={`${prefix}-search`}
                    value={search}
                    disabled={busy}
                    className={controls}
                    onChange={event => {
                      setSearch(event.target.value);
                      setSelectedIds([]);
                      setConfirmation('');
                    }}
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  disabled={busy || filtered.length > 100}
                  className="min-h-12 rounded-lg bg-surface-container-high px-4 font-bold disabled:opacity-50"
                  onClick={() => {
                    setSelectedIds(filtered.map(item => item.id));
                    setConfirmation('');
                  }}
                >
                  {t(words.selectVisible)}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  className="min-h-12 px-4 text-primary font-bold"
                  onClick={() => {
                    setSelectedIds([]);
                    setConfirmation('');
                  }}
                >
                  {t(words.clear)}
                </button>
              </div>
              <div className="max-h-96 space-y-3 overflow-y-auto rounded-xl bg-surface p-3">
                {filtered.length === 0 && <p>{t(words.empty)}</p>}
                {filtered.map(item => (
                  <label
                    key={item.id}
                    className="flex min-h-12 cursor-pointer items-start gap-3 rounded-xl bg-surface-container-low p-4"
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(item.id)}
                      disabled={busy}
                      className="mt-1 size-5 shrink-0 accent-primary"
                      onChange={event => {
                        setSelectedIds(ids =>
                          event.target.checked
                            ? [...ids, item.id]
                            : ids.filter(id => id !== item.id)
                        );
                        setConfirmation('');
                      }}
                    />
                    <span className="min-w-0 break-words">
                      <span className="block font-bold">
                        {item.name}
                        {item.code ? ` (${item.code})` : ''}
                      </span>
                      <span className="block text-sm text-on-surface-variant">{item.location}</span>
                      <span className="mt-2 block font-semibold text-primary">
                        {t(item.accessGrant?.active ? words.active : words.ordinary)}
                      </span>
                      {item.recognition && (
                        <span className="block text-sm">
                          {t(words.recognized).replace('{score}', String(item.recognition.score))}
                        </span>
                      )}
                      {item.accessGrant && (
                        <span className="block text-sm text-on-surface-variant">
                          {item.accessGrant.reason} · {item.accessGrant.updatedAt}
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
              <p className="font-bold">
                {t(words.selected).replace('{count}', String(selected.length))}
              </p>
              <label className="flex min-h-12 items-start gap-3 font-semibold">
                <input
                  type="checkbox"
                  checked={allSessions}
                  disabled={busy}
                  className="mt-1 size-5 shrink-0 accent-primary"
                  onChange={event => {
                    setAllSessions(event.target.checked);
                    setConfirmation('');
                  }}
                />
                {t(words.allSessions)}
              </label>
              <p className="leading-relaxed text-on-surface-variant">{t(words.sessionsHint)}</p>
              <div>
                <label htmlFor={`${prefix}-reason`} className="mb-2 block font-semibold">
                  {t(words.reason)}
                </label>
                <textarea
                  id={`${prefix}-reason`}
                  value={reason}
                  maxLength={1000}
                  disabled={busy}
                  className={controls}
                  onChange={event => setReason(event.target.value)}
                />
              </div>
              <div>
                <label htmlFor={`${prefix}-confirm`} className="mb-2 block font-semibold">
                  {t(words.confirmation)}
                </label>
                <input
                  id={`${prefix}-confirm`}
                  value={confirmation}
                  disabled={busy}
                  className={controls}
                  autoComplete="off"
                  onChange={event => setConfirmation(event.target.value)}
                />
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  disabled={busy || !valid || confirmation !== 'BUKA'}
                  className="min-h-12 rounded-lg bg-gradient-to-r from-primary to-primary-container px-5 font-bold text-on-primary transition-transform active:scale-95 duration-150 disabled:opacity-50"
                  onClick={() => submit('BUKA')}
                >
                  {t(words.grant)}
                </button>
                <button
                  type="button"
                  disabled={
                    busy ||
                    !valid ||
                    confirmation !== 'CABUT' ||
                    selected.some(item => !item.accessGrant?.active)
                  }
                  className="min-h-12 rounded-lg bg-error-container px-5 font-bold text-on-error-container transition-transform active:scale-95 duration-150 disabled:opacity-50"
                  onClick={() => submit('CABUT')}
                >
                  {t(words.revoke)}
                </button>
              </div>
              <p className="text-sm leading-relaxed text-on-surface-variant">
                {t(words.revokeHint)}
              </p>
              {saved && (
                <p
                  role="status"
                  className="rounded-xl bg-success-container p-4 text-on-success-container"
                >
                  {t(words.saved)}
                </p>
              )}
              {mutation.isError && (
                <div
                  role="alert"
                  className="rounded-xl bg-error-container p-4 text-on-error-container"
                >
                  <p>{t(words.failed)}</p>
                  <p>{mutation.error instanceof Error ? mutation.error.message : ''}</p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
